import clsx from "clsx";
import { ChevronDown, ChevronUp, type LucideIcon, Search, X } from "lucide-react";
import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  createElement,
  isValidElement,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type Ref,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { type Awaiting, awaitingAttr } from "./awaiting";
import { motion, type Origin, useAnimatedHeight, useClosing } from "./presence";
import { ScrollFades, useScrollEdges } from "./scrollEdges";
import { t, useLang } from "../i18n/i18n";

/** Briques d'interface partagées (design system Generex, règles d'harmonisation R0-R16, docs/02 § 4.6).
 *  Une seule façon de faire par motif : un besoin nouveau étend ces composants au lieu d'en recopier un
 *  localement. Tailles : jetons `text-caps` … `text-display` d'index.css, jamais de taille libre (`text-` suivi
 *  d'une valeur entre crochets) ; `className` des boutons, badges, champs et titres réservé aux marges et à la
 *  largeur (`npm run ui-check`, règle h).
 *
 *  Ordre des classes : Tailwind émet les tailles libres AVANT les jetons (tri alphabétique à propriété égale). Une
 *  taille, une hauteur ou une graisse passée en `className` à un composant qui pose déjà la sienne est donc SANS
 *  EFFET (ou incertaine) : passer par ses props (`size`, `variant`) ou par la variante de classe prévue
 *  (`inputSmClass`, `inputTitleClass`, `codeInputClass`). */

// --- Icônes -------------------------------------------------------------------------------------------------------

/** Icône passée à un composant : un élément déjà rendu (`<Map size={16} />`) ou le composant Lucide lui-même
 *  (`Map`), rendu alors à la taille de l'emplacement (12 pastille, 14 bouton ou ligne, 16 en-tête, 24 état vide). */
export type IconProp = ReactNode | LucideIcon;

/** Rend une `IconProp` : l'élément tel quel, ou le composant à la taille demandée. */
export function renderIcon(icon: IconProp, size: number, className?: string): ReactNode {
  if (icon === null || icon === undefined || typeof icon === "boolean") return null;
  if (isValidElement(icon) || typeof icon === "string" || typeof icon === "number") return icon as ReactNode;
  // Composant : fonction, ou objet `forwardRef` / `memo` (les icônes Lucide sont des `forwardRef`).
  if (typeof icon === "function" || (typeof icon === "object" && ("render" in icon || "type" in icon)))
    return createElement(icon as LucideIcon, { size, className, "aria-hidden": true });
  return icon as ReactNode;
}

// --- Rubriques et étiquettes --------------------------------------------------------------------------------------

/** Titre de rubrique en petites capitales espacées (« VUES », « DOCUMENTS ») : barre latérale, rubriques de vue,
 *  menus (`MenuLabel`). Style figé ; les capitales viennent du composant, le libellé s'écrit en casse normale dans
 *  `t()`. `trailing` : bouton à droite, comme le « + » de Prevail ; `inset` : sans marge propre (dans une carte ou
 *  un menu) ; `as` : balise sémantique (titre `h2`/`h3`, en-tête de tableau `th`). */
export function SectionLabel({
  children,
  className,
  as = "div",
  inset = false,
  trailing,
  tone = "muted",
}: {
  children: ReactNode;
  /** Marges et alignement seulement. */
  className?: string;
  as?: "div" | "h2" | "h3" | "p" | "th";
  inset?: boolean;
  trailing?: ReactNode;
  tone?: "muted" | "accent";
}) {
  const style = clsx(
    "text-caps font-semibold tracking-[0.08em] uppercase",
    tone === "accent" ? "text-accent" : "text-ink-muted",
    !inset && "px-3 pt-4 pb-1.5",
    trailing !== undefined && "flex items-center justify-between gap-2",
    className,
  );
  if (trailing === undefined) return createElement(as, { className: style }, children);
  return createElement(as, { className: style }, <span className="min-w-0 truncate">{children}</span>, trailing);
}

type BadgeTone = "neutral" | "accent" | "ok" | "warn" | "danger" | "live" | "run" | "friend" | "hostile" | "brand";

const BADGE_SOFT: Record<Exclude<BadgeTone, "brand">, string> = {
  neutral: "border-line-strong bg-surface text-ink-soft",
  accent: "border-transparent bg-accent-soft text-accent",
  ok: "border-transparent bg-ok-soft text-ok",
  warn: "border-transparent bg-warn-soft text-warn",
  danger: "border-transparent bg-danger-soft text-danger",
  live: "border-transparent bg-live-soft text-live",
  run: "border-transparent bg-run-soft text-run",
  friend: "border-transparent bg-friend-soft text-friend",
  hostile: "border-transparent bg-hostile-soft text-hostile",
};
const BADGE_SOLID: Record<Exclude<BadgeTone, "brand">, string> = {
  neutral: "border-transparent bg-ink-soft text-white",
  accent: "border-transparent bg-accent text-white",
  ok: "border-transparent bg-ok text-white",
  warn: "border-transparent bg-warn text-white",
  danger: "border-transparent bg-danger text-white",
  live: "border-transparent bg-live text-white",
  run: "border-transparent bg-run text-white",
  friend: "border-transparent bg-friend text-white",
  hostile: "border-transparent bg-hostile text-white",
};

/** Pastille (R6) : compteur neutre à droite d'une ligne, sans parenthèses ; état d'exception avec `dot` (point et
 *  libellé sur fond pâle) — l'état normal reste en texte gris, sans pastille. Tons : `neutral`, `accent`
 *  (sélection, « Modifié »), `ok`, `warn`, `danger`, `live` (en direct), `run` (IA en cours), `friend`, `hostile`
 *  (`brand` : ancien nom de `hostile`). `size` : `md` (20 px, défaut) ou `sm` (16 px, dans une ligne dense) ;
 *  `variant` : `soft` (défaut) ou `solid`. Jamais de taille ni de police par `className` (marges seulement). */
/** Version anglaise en bêta (demande d'Alexis du 26/10/2026) : pastille « BETA » sur chaque vue (panneaux, volets,
 *  accueil, écrans de licence ; pas dans la barre latérale, déjà pleine) tant que l'interface est en anglais ;
 *  rien en français. */
export function BetaBadge({ className }: { className?: string }) {
  const english = useLang() === "en";
  if (!english) return null;
  return (
    <Badge
      size="sm"
      tone="accent"
      className={className}
      title={t("Version anglaise en cours de finalisation : signalez toute traduction à revoir.")}
    >
      {t("BÊTA")}
    </Badge>
  );
}

export function Badge({
  children,
  tone = "neutral",
  className,
  title,
  dot = false,
  size = "md",
  variant = "soft",
}: {
  children: ReactNode;
  tone?: BadgeTone;
  /** Marges seulement. */
  className?: string;
  /** Info-bulle portée par le badge lui-même (une enveloppe en ligne le décalerait sur la ligne de base). */
  title?: string;
  dot?: boolean;
  size?: "sm" | "md";
  variant?: "soft" | "solid";
}) {
  const key = tone === "brand" ? "hostile" : tone;
  return (
    <span
      title={title}
      className={clsx(
        "inline-flex shrink-0 items-center justify-center rounded-md border leading-none font-medium whitespace-nowrap tabular-nums",
        size === "sm" ? "h-4 min-w-4 px-1 text-caps" : "h-5 min-w-5 px-1.5 text-meta",
        (variant === "solid" ? BADGE_SOLID : BADGE_SOFT)[key],
        dot && "gap-1.5",
        className,
      )}
    >
      {dot && <span className="size-1.5 shrink-0 rounded-full bg-current" aria-hidden />}
      {children}
    </span>
  );
}

/** Étiquette (R6) : catégorie, source, type (« Raster », « On demand » de Prevail) — fond `canvas`, sans bordure.
 *  Avec `onClick`, l'étiquette est un bouton. */
export function Chip({
  children,
  icon,
  title,
  onClick,
  className,
}: {
  children: ReactNode;
  /** Icône de 12 px. */
  icon?: IconProp;
  title?: string;
  onClick?: () => void;
  /** Marges seulement. */
  className?: string;
}) {
  const style = clsx(
    "inline-flex h-6 max-w-full shrink-0 items-center gap-1 rounded-md bg-canvas px-2 text-meta whitespace-nowrap text-ink-soft",
    onClick && "transition-colors hover:bg-hover hover:text-ink",
    className,
  );
  const body = (
    <>
      {icon !== undefined && <span className="flex shrink-0 text-ink-muted">{renderIcon(icon, 12)}</span>}
      <span className="min-w-0 truncate">{children}</span>
    </>
  );
  return onClick ? (
    <button type="button" title={title} onClick={onClick} className={style}>
      {body}
    </button>
  ) : (
    <span title={title} className={style}>
      {body}
    </span>
  );
}

/** Le poste est-il un Mac (ou un iPad) : ⌘ plutôt que Ctrl dans les raccourcis affichés. */
export function isApplePlatform(): boolean {
  if (typeof navigator === "undefined") return true;
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ?? navigator.platform ?? "";
  return /mac|iphone|ipad|ipod/i.test(platform) || /Mac OS X/.test(navigator.userAgent);
}

/** Raccourci avec la touche de commande de la plateforme : `shortcut("K")` → « ⌘K » sur Mac, « Ctrl K » ailleurs. */
export function shortcut(key: string): string {
  return isApplePlatform() ? `⌘${key}` : `Ctrl ${key}`;
}

/** Raccourci « Rétablir » de la plateforme : « ⇧⌘Z » sur Mac, « Ctrl Maj Z » ailleurs (« Ctrl Shift Z » en anglais). */
export function redoShortcut(): string {
  return isApplePlatform() ? "⇧⌘Z" : `Ctrl ${t("Maj")} Z`;
}

/** Touche de raccourci (R5), libellé selon la plateforme (`shortcut`). Les raccourcis sont masqués au doigt
 *  (pointeur grossier), mais une touche seule masquée laisserait des fragments orphelins (« ou », « terminer la
 *  mesure ») : on masque donc le GROUPE entier.
 *
 *  - Raccourci isolé (à droite d'une entrée de menu, d'un champ de recherche, de la barre latérale) :
 *    `<Kbd hideOnTouch>`.
 *  - Indication « touche + texte » (« ou Entrée », « M mesurer distance et temps ») : `<KbdHint>` autour du tout.
 *  - Touche citée dans une phrase d'aide (« Appuyez sur ? ») : `<Kbd>` seul, visible partout. */
export function Kbd({ children, hideOnTouch = false }: { children: ReactNode; hideOnTouch?: boolean }) {
  return (
    <kbd
      className={clsx(
        "rounded border border-line-strong bg-canvas px-1.5 py-0.5 font-mono text-caps text-ink-muted",
        hideOnTouch && "pointer-coarse:hidden",
      )}
    >
      {children}
    </kbd>
  );
}

/** Indication de raccourci complète (touches et texte), masquée en bloc au doigt (R5) : « ou <Kbd>Entrée</Kbd> »,
 *  « <Kbd>M</Kbd> mesurer distance et temps ». `className` : mise en page et marges. */
export function KbdHint({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={clsx("pointer-coarse:hidden", className)}>{children}</span>;
}

// --- Boutons ------------------------------------------------------------------------------------------------------

/** Props communes aux boutons qui peuvent être bloqués (R12) : un préalable manque (dossier non enregistré,
 *  champ vide). Le bouton reste cliquable au doigt comme à la souris, porte `aria-disabled`, paraît désactivé
 *  (40 %) sans effet de survol ni de pression, et son clic appelle `onBlocked` — qui désigne l'action attendue
 *  (`designate()`, components/attention.ts) — jamais `onClick`. `disabled` reste réservé à une requête en cours. */
export interface BlockableProps {
  blocked?: boolean;
  onBlocked?: (event: ReactMouseEvent<HTMLElement>) => void;
}

/** Gestionnaire de clic d'un contrôle bloquable. */
function guardClick<E extends HTMLElement>(
  blocked: boolean | undefined,
  onBlocked: BlockableProps["onBlocked"],
  onClick: ((event: ReactMouseEvent<E>) => void) | undefined,
) {
  return (event: ReactMouseEvent<E>) => {
    if (blocked) {
      event.preventDefault();
      onBlocked?.(event);
      return;
    }
    onClick?.(event);
  };
}

export type IconButtonSize = "xs" | "sm" | "md";
const ICON_SIZE: Record<IconButtonSize, string> = { xs: "size-6 rounded-md", sm: "size-7 rounded-lg", md: "size-8 rounded-lg" };
const ICON_VARIANT = {
  ghost: { base: "text-ink-soft", hover: "hover:bg-hover hover:text-ink" },
  outline: { base: "border border-line-strong bg-surface text-ink-soft", hover: "hover:bg-hover hover:text-ink" },
  primary: { base: "bg-ink text-white", hover: "hover:bg-ink/90" },
  danger: { base: "text-danger", hover: "hover:bg-danger-soft" },
} as const;

function iconButtonClass(
  size: IconButtonSize,
  variant: keyof typeof ICON_VARIANT,
  active: boolean | undefined,
  blocked: boolean | undefined,
  className: string | undefined,
) {
  return clsx(
    "inline-flex items-center justify-center transition-colors disabled:opacity-40",
    ICON_SIZE[size],
    ICON_VARIANT[variant].base,
    blocked ? "opacity-40" : ICON_VARIANT[variant].hover,
    active && variant !== "primary" && "bg-selected text-ink",
    className,
  );
}

/** Bouton icône (R5) : `size` `xs` (24 px, dans une ligne), `sm` (28 px, en-têtes de panneau et barres denses),
 *  `md` (32 px, défaut) ; `variant` `ghost` (défaut), `outline`, `primary` (fond noir) ou `danger` (icône rouge
 *  d'une suppression, comme le bouton `danger-ghost`). Icône de 14 px (16 px
 *  pour la fermeture et dans les en-têtes). `title` ou `aria-label` obligatoire pour être lu. */
export function IconButton({
  className,
  active,
  size = "md",
  variant = "ghost",
  blocked,
  onBlocked,
  onClick,
  type = "button",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> &
  BlockableProps & {
    active?: boolean;
    size?: IconButtonSize;
    variant?: keyof typeof ICON_VARIANT;
    ref?: Ref<HTMLButtonElement>;
  }) {
  return (
    <button
      type={type}
      aria-disabled={blocked || undefined}
      data-variant={variant}
      onClick={guardClick(blocked, onBlocked, onClick)}
      className={iconButtonClass(size, variant, active, blocked, className)}
      {...props}
    />
  );
}

/** Bouton icône sur un lien (`<a>`) : téléchargement, page externe. Même rendu qu'`IconButton`, `blocked` +
 *  `onBlocked` compris (le lien n'est pas suivi). */
export function IconLink({
  className,
  size = "md",
  variant = "ghost",
  blocked,
  onBlocked,
  onClick,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> &
  BlockableProps & {
    size?: IconButtonSize;
    variant?: keyof typeof ICON_VARIANT;
    ref?: Ref<HTMLAnchorElement>;
  }) {
  return (
    <a
      data-variant={variant}
      aria-disabled={blocked || undefined}
      onClick={guardClick(blocked, onBlocked, onClick)}
      className={iconButtonClass(size, variant, false, blocked, className)}
      {...props}
    />
  );
}

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "danger-ghost" | "link";
export type ButtonSize = "sm" | "md" | "lg";

const BUTTON_VARIANT: Record<ButtonVariant, { base: string; hover: string }> = {
  primary: { base: "bg-ink text-white", hover: "hover:bg-ink/90" },
  secondary: { base: "border border-line-strong bg-surface text-ink", hover: "hover:bg-hover" },
  ghost: { base: "text-ink-soft", hover: "hover:bg-hover hover:text-ink" },
  danger: { base: "bg-danger text-white", hover: "hover:bg-danger/90" },
  "danger-ghost": { base: "text-danger", hover: "hover:bg-danger-soft" },
  link: { base: "text-accent", hover: "hover:underline" },
};
const BUTTON_SIZE: Record<ButtonSize, string> = { sm: "h-7 px-2.5", md: "h-8 px-3", lg: "h-9 px-4" };

function buttonClass(
  variant: ButtonVariant,
  size: ButtonSize,
  highlight: boolean | undefined,
  blocked: boolean | undefined,
  className: string | undefined,
) {
  return clsx(
    variant === "link"
      ? "inline-flex items-center gap-1.5 rounded-sm text-left text-ui font-medium transition-colors disabled:opacity-40"
      : clsx(
          "inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg text-ui font-medium whitespace-nowrap transition-colors disabled:opacity-40",
          BUTTON_SIZE[size],
        ),
    BUTTON_VARIANT[variant].base,
    blocked ? "opacity-40" : BUTTON_VARIANT[variant].hover,
    highlight && !blocked && "wayfinding",
    className,
  );
}

/** Bouton (R5). Variantes : `primary` (noir, un seul par zone, défaut), `secondary` (« Annuler », pied de fiche),
 *  `ghost`, `danger` (suppression dans une fenêtre), `danger-ghost` (suppression dans une fiche), `link` (aller à
 *  une autre vue, sans hauteur). Tailles : `sm` 28 px (bandeaux, barres denses, pied de fiche), `md` 32 px
 *  (défaut), `lg` 36 px (accueil). `highlight` : il pulse, seulement s'il est l'action attendue. `blocked` +
 *  `onBlocked` : préalable manquant (voir `BlockableProps`). Icône de 14 px. `className` : marges et largeur
 *  seulement — jamais de hauteur, de taille ni de police. */
export function Button({
  className,
  variant = "primary",
  size = "md",
  highlight,
  blocked,
  onBlocked,
  onClick,
  type = "button",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> &
  BlockableProps & {
    variant?: ButtonVariant;
    size?: ButtonSize;
    highlight?: boolean;
    ref?: Ref<HTMLButtonElement>;
  }) {
  return (
    <button
      type={type}
      aria-disabled={blocked || undefined}
      data-variant={variant}
      onClick={guardClick(blocked, onBlocked, onClick)}
      className={buttonClass(variant, size, highlight, blocked, className)}
      {...props}
    />
  );
}

/** Bouton sur un lien (`<a>`, téléchargement ou page externe) : même rendu que `Button`. `blocked` + `onBlocked` :
 *  comme `Button` (le lien n'est pas suivi, son clic appelle `onBlocked`). */
export function ButtonLink({
  className,
  variant = "primary",
  size = "md",
  highlight,
  blocked,
  onBlocked,
  onClick,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> &
  BlockableProps & {
    variant?: ButtonVariant;
    size?: ButtonSize;
    highlight?: boolean;
    ref?: Ref<HTMLAnchorElement>;
  }) {
  return (
    <a
      data-variant={variant}
      aria-disabled={blocked || undefined}
      onClick={guardClick(blocked, onBlocked, onClick)}
      className={buttonClass(variant, size, highlight, blocked, className)}
      {...props}
    />
  );
}

// --- Panneau flottant ---------------------------------------------------------------------------------------------

/** Largeurs des panneaux flottants (R7) : vue de la colonne gauche 380 px, Calques 300 px, fiche d'objet 340 px,
 *  assistant IA 360 px. */
export const PANEL_WIDTH = {
  view: "w-[380px]",
  layers: "w-[300px]",
  card: "w-[340px]",
  assistant: "w-[360px]",
} as const;
export type PanelWidth = keyof typeof PANEL_WIDTH;

/** Panneau flottant sur la carte (calques, vues, fiche d'objet). Avec `onToggle`, l'en-tête entier déplie ou replie le contenu (chevron).
 *  Il apparaît en glissant depuis son bord (`enter` : « left » pour la colonne gauche, « right » pour la fiche) ;
 *  son contenu se déplie en hauteur, en fondu.
 *
 *  - `icon` : icône du titre (celle de la navigation, `VIEW_META`), 16 px en `ink-muted`.
 *  - `actions` : boutons d'en-tête en `IconButton size="sm"`, « ? » (`HelpButton buttonSize="sm"`) en dernier.
 *  - `footer` : barre d'action collante, rendue après la zone qui défile (toujours visible) ; on y pose les
 *    boutons directement (rangée `px-4 py-3`).
 *  - `width` : `view` (380), `layers` (300), `card` (340) ou `assistant` (360) ; par défaut `view` à gauche et
 *    `card` à droite. Une largeur `w-…` déjà passée dans `className` (appels d'avant ce composant) l'emporte. */
export function FloatingPanel({
  title,
  icon,
  actions,
  footer,
  children,
  className,
  open = true,
  onToggle,
  tallHeader = false,
  enter = "fade",
  width,
}: {
  title: ReactNode;
  icon?: IconProp;
  actions?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
  open?: boolean;
  onToggle?: () => void;
  /** En-tête à la hauteur de son titre (symbole et deux lignes), avec une marge au-dessus et au-dessous. */
  tallHeader?: boolean;
  /** Côté d'où le panneau apparaît. */
  enter?: Origin;
  width?: PanelWidth;
}) {
  const expanded = !onToggle || open;
  // Panneau d'une vue posé sur la carte (à gauche) : affiché par défaut, l'utilisateur peut le réduire à son
  // en-tête pour dégager la carte, puis l'afficher de nouveau (bouton de l'en-tête ou clic sur le titre).
  const reducible = !onToggle && enter === "left";
  const [reduced, setReduced] = useState(false);
  const shown = !reducible || !reduced;
  const closing = useClosing();
  // Contenu qui change (rapport, formulaire, fiche) : hauteur animée ; le volet repliable a sa propre animation.
  const panelRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  useAnimatedHeight(panelRef, contentRef, !onToggle);
  // Contenu plus haut que le panneau : fondus des bords (rien ne bute contre le filet du pied ou le bord).
  const edges = useScrollEdges(scrollRef, onToggle ? null : "panel");
  const legacyWidth = /(^|\s)w-/.test(className ?? "");
  const resolvedWidth = width ?? (legacyWidth ? undefined : enter === "left" ? "view" : enter === "right" ? "card" : undefined);
  // Sans `icon`, le titre garde sa structure d'avant (les appelants y composent leur propre icône).
  const heading = (
    <div className="flex min-w-0 items-center gap-2">
      {icon !== undefined ? (
        <h2 className="flex min-w-0 items-center gap-2 text-ui font-semibold text-ink">
          <span className="flex shrink-0 text-ink-muted">{renderIcon(icon, 16)}</span>
          <span className="min-w-0">{title}</span>
        </h2>
      ) : (
        <h2 className="min-w-0 text-ui font-semibold text-ink">{title}</h2>
      )}
      <BetaBadge />
    </div>
  );
  const footerBar = footer !== undefined && footer !== null && (
    <div className="flex shrink-0 items-center gap-2 border-t border-line bg-surface px-4 py-3">{footer}</div>
  );
  return (
    <section
      ref={panelRef}
      data-floating-panel=""
      className={clsx(
        "pointer-events-auto flex max-h-full flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-float",
        resolvedWidth && clsx(PANEL_WIDTH[resolvedWidth], "max-w-full"),
        motion(enter, closing),
        className,
      )}
    >
      <header
        className={clsx(
          "flex shrink-0 items-center justify-between border-b px-4 transition-colors duration-(--gx-enter)",
          tallHeader ? "min-h-9 gap-3 py-3" : "h-9",
          expanded && shown ? "border-line" : "border-transparent",
        )}
      >
        {onToggle ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            title={expanded ? t("Replier") : t("Déplier")}
            className="focus-inset -mx-4 flex h-full min-w-0 flex-1 items-center justify-between gap-2 px-4 text-left transition-colors hover:bg-hover"
          >
            {heading}
            <ChevronDown size={16} className={clsx("shrink-0 text-ink-muted transition-transform", expanded && "rotate-180")} />
          </button>
        ) : reducible && reduced ? (
          <button
            type="button"
            onClick={() => setReduced(false)}
            title={t("Afficher le panneau")}
            className="focus-inset -ml-4 flex h-full min-w-0 flex-1 items-center pl-4 text-left transition-colors hover:bg-hover"
          >
            {heading}
          </button>
        ) : (
          heading
        )}
        {(actions || reducible) && (
          <div className="flex items-center gap-0.5">
            {actions}
            {reducible && (
              <IconButton
                size="sm"
                onClick={() => setReduced((r) => !r)}
                title={reduced ? t("Afficher le panneau") : t("Réduire le panneau")}
                aria-label={reduced ? t("Afficher le panneau") : t("Réduire le panneau")}
                aria-expanded={!reduced}
              >
                {reduced ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </IconButton>
            )}
          </div>
        )}
      </header>
      {onToggle ? (
        <div className="gx-collapse min-h-0 flex-1" data-open={expanded} inert={!expanded}>
          {footerBar ? (
            <div className="flex min-h-0 flex-col">
              <div className="min-h-0 overflow-y-auto">{children}</div>
              {footerBar}
            </div>
          ) : (
            <div className="overflow-y-auto">{children}</div>
          )}
        </div>
      ) : (
        <div className="gx-collapse min-h-0 flex-1" data-open={shown} inert={!shown}>
          <div className="flex min-h-0 flex-col overflow-hidden">
            <div className="relative flex min-h-0 flex-1 flex-col">
              <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
                <div ref={contentRef}>{children}</div>
              </div>
              <ScrollFades edges={edges} />
            </div>
            {footerBar}
          </div>
        </div>
      )}
    </section>
  );
}

// --- Champs -------------------------------------------------------------------------------------------------------

/** Champ libellé (R8) : libellé en 12 px au-dessus, aide en 11,5 px dessous. Le contrôle est l'enfant (`<input
 *  className={inputClass}>`, `Select`, `textarea` en `textareaClass`). */
export function Field({
  label,
  children,
  hint,
  className,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
  /** Marges seulement. */
  className?: string;
}) {
  return (
    <label className={clsx("block", className)}>
      <span className="mb-1 block text-ui font-medium text-ink-soft">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-meta leading-snug text-ink-muted">{hint}</span>}
    </label>
  );
}

const INPUT_BASE =
  "w-full rounded-lg border border-line-strong bg-surface px-2.5 text-ink outline-none transition-shadow focus:border-accent focus:ring-2 focus:ring-accent/15 disabled:bg-canvas disabled:text-ink-muted";

/** Champ de saisie (32 px) : bordure `line-strong`, focus en bordure `accent` avec un anneau `accent/15`. Une
 *  taille ou une hauteur ajoutée par `clsx(inputClass, …)` est sans effet (voir l'en-tête) : choisir la variante. */
export const inputClass = `h-8 ${INPUT_BASE} text-ui`;
/** Champ dense (28 px, texte 11,5 px) : barres d'outils, lignes de tableau. */
export const inputSmClass = `h-7 ${INPUT_BASE} text-meta`;
/** Champ de titre (32 px, texte 13 px) : renommage d'un document, d'un ordre ou d'un dossier en place. */
export const inputTitleClass = `h-8 ${INPUT_BASE} text-title`;
/** Champ de code (32 px, chasse fixe 15 px, chiffres tabulaires) : code de transmission, code à saisir. L'espacement
 *  et la casse (`tracking-…`, `uppercase`) restent à l'appelant. */
export const codeInputClass = `h-8 ${INPUT_BASE} font-mono text-heading tabular-nums`;
/** Zone de texte : même bordure, hauteur libre, redimensionnable en hauteur. */
export const textareaClass = `h-auto ${INPUT_BASE} py-1.5 text-ui leading-snug resize-y`;

/** Champ de recherche (R8) : loupe de 14 px, saisie en 12 px, bouton d'effacement dès qu'il y a du texte, touche
 *  de raccourci facultative (`kbd`, masquée au doigt). `size` : `md` 32 px (défaut) ou `sm` 28 px. Échap dans le
 *  champ efface d'abord le texte (l'appui est alors consommé : rien d'autre ne se ferme). */
export function SearchField({
  value,
  onChange,
  placeholder,
  ariaLabel,
  kbd,
  size = "md",
  onClear,
  autoFocus,
  inputRef,
  onKeyDown,
  className,
  id,
  combobox,
  awaiting,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Libellé lu par les lecteurs d'écran (par défaut le texte d'attente). */
  ariaLabel?: string;
  kbd?: ReactNode;
  size?: "sm" | "md";
  /** Effacement (par défaut `onChange("")`). */
  onClear?: () => void;
  autoFocus?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  onKeyDown?: (event: ReactKeyboardEvent<HTMLInputElement>) => void;
  /** Marges et largeur seulement. */
  className?: string;
  id?: string;
  /** Champ qui propose une liste de résultats (motif combobox) : liste pilotée, ouverte ou non, élément actif. */
  combobox?: { listId: string; expanded: boolean; activeId?: string };
  /** Champ à renseigner (`components/awaiting.ts`) : surbrillance du cadre tant que la saisie manque. */
  awaiting?: Awaiting;
}) {
  const local = useRef<HTMLInputElement | null>(null);
  const setRef = (node: HTMLInputElement | null) => {
    local.current = node;
    if (typeof inputRef === "function") inputRef(node);
    else if (inputRef) (inputRef as { current: HTMLInputElement | null }).current = node;
  };
  const clear = () => {
    if (onClear) onClear();
    else onChange("");
    local.current?.focus();
  };
  return (
    <label
      {...awaitingAttr(awaiting)}
      className={clsx(
        "flex items-center gap-2 rounded-lg border border-line-strong bg-surface px-2.5 transition-shadow",
        "focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/15",
        size === "sm" ? "h-7" : "h-8",
        className,
      )}
    >
      <Search size={14} className="shrink-0 text-ink-muted" aria-hidden />
      <input
        ref={setRef}
        id={id}
        type="search"
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        role={combobox ? "combobox" : undefined}
        aria-autocomplete={combobox ? "list" : undefined}
        aria-controls={combobox?.expanded ? combobox.listId : undefined}
        aria-expanded={combobox ? combobox.expanded : undefined}
        aria-activedescendant={combobox?.expanded ? combobox.activeId : undefined}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          if (e.defaultPrevented) return;
          if (e.key === "Escape" && value) {
            e.preventDefault();
            e.stopPropagation();
            clear();
          }
        }}
        className="min-w-0 flex-1 appearance-none bg-transparent text-ui text-ink outline-none placeholder:text-ink-muted [&::-webkit-search-cancel-button]:appearance-none"
      />
      {value ? (
        <button
          type="button"
          onClick={clear}
          title={t("Effacer la recherche")}
          aria-label={t("Effacer la recherche")}
          className="inline-flex size-5 shrink-0 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-hover hover:text-ink"
        >
          <X size={12} />
        </button>
      ) : (
        kbd !== undefined && <span className="shrink-0">{typeof kbd === "string" ? <Kbd hideOnTouch>{kbd}</Kbd> : kbd}</span>
      )}
    </label>
  );
}

// --- Choix exclusifs ----------------------------------------------------------------------------------------------

/** Option d'un `Segmented`. `dot` : couleur d'un point devant le libellé (`var(--color-ok)`) ; `count` : compteur
 *  en `Badge`. */
export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: IconProp;
  dot?: string;
  count?: number;
  title?: string;
  disabled?: boolean;
  /** Préalable manquant (R12) : segment estompé et `aria-disabled`, mais son choix appelle quand même `onChange`,
   *  qui désigne l'action attendue au lieu de changer la valeur. */
  blocked?: boolean;
}

/** Option qui reçoit la tabulation (une seule dans le groupe) : la choisie, sinon la première disponible. */
function focusable<T extends string>(options: readonly { value: T; disabled?: boolean }[], value: T): number {
  const selected = options.findIndex((o) => o.value === value && !o.disabled);
  return selected >= 0 ? selected : options.findIndex((o) => !o.disabled);
}

/** Déplacement aux flèches entre options d'un groupe (radio ou onglets) : l'option suivante non désactivée est
 *  choisie et mise au point, comme un groupe de boutons radio natif. */
function arrowNavigate<T extends string>(
  event: ReactKeyboardEvent<HTMLElement>,
  options: readonly { value: T; disabled?: boolean }[],
  value: T,
  onChange: (value: T) => void,
) {
  const keys: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
  let target = -1;
  const enabled = options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0);
  if (!enabled.length) return;
  if (event.key === "Home") target = enabled[0]!;
  else if (event.key === "End") target = enabled[enabled.length - 1]!;
  else if (keys[event.key]) {
    const current = enabled.indexOf(options.findIndex((o) => o.value === value));
    const next = (current < 0 ? 0 : current + keys[event.key]! + enabled.length) % enabled.length;
    target = enabled[next]!;
  } else return;
  event.preventDefault();
  onChange(options[target]!.value);
  const buttons = event.currentTarget.querySelectorAll<HTMLElement>("[data-option]");
  buttons[target]?.focus();
}

/** Choix exclusif en segments (R8) : rail `canvas`, pouce blanc, texte `accent`. `label` au-dessus (sinon
 *  `ariaLabel` pour les lecteurs d'écran) ; `size` `md` 28 px (défaut) ou `sm` 24 px ; `role` `radio` (défaut,
 *  un réglage) ou `tab` (bascule d'affichage) ; `fill` : segments de largeur égale sur toute la largeur (défaut),
 *  sinon chacun à la largeur de son libellé. Flèches, Début et Fin au clavier. */
export function Segmented<T extends string>({
  label,
  ariaLabel,
  options,
  value,
  onChange,
  size = "md",
  role = "radio",
  fill = true,
  slide = false,
  className,
}: {
  label?: string;
  ariaLabel?: string;
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: "sm" | "md";
  role?: "radio" | "tab";
  /** Toute la largeur : segments égaux (`true`), ou à leur largeur naturelle, la place restante répartie
   *  (`"natural"` : un libellé long comme « Advanced » n'est pas coupé dans un sélecteur de largeur fixe). */
  fill?: boolean | "natural";
  /** Fond du segment actif qui glisse d'un segment à l'autre (`--gx-slide`) au lieu de changer de place d'un coup. */
  slide?: boolean;
  /** Marges et largeur seulement. */
  className?: string;
}) {
  const tabs = role === "tab";
  const groupRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<{ x: number; width: number; animate: boolean } | null>(null);
  useLayoutEffect(() => {
    const group = groupRef.current;
    if (!slide || !group) return;
    // Première mesure posée sans glissement ; seuls les changements de valeur glissent.
    const measure = (animate: boolean) => {
      const selected = group.querySelector<HTMLElement>("[data-option][data-selected]");
      setThumb((previous) =>
        selected ? { x: selected.offsetLeft, width: selected.offsetWidth, animate: animate && previous !== null } : null,
      );
    };
    measure(true);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => measure(false));
    observer?.observe(group);
    return () => observer?.disconnect();
  }, [slide, value]);
  return (
    <div className={className}>
      {label && <span className="mb-1 block text-ui font-medium text-ink-soft">{label}</span>}
      <div
        ref={groupRef}
        className={clsx("rounded-lg bg-canvas p-0.5", fill ? "flex" : "inline-flex max-w-full", slide && "relative")}
        role={tabs ? "tablist" : "radiogroup"}
        aria-label={label ?? ariaLabel}
        onKeyDown={(e) => arrowNavigate(e, options, value, onChange)}
      >
        {slide && thumb && (
          <span
            aria-hidden
            data-segmented-thumb=""
            className={clsx(
              "pointer-events-none absolute top-0.5 bottom-0.5 left-0 rounded-md bg-surface shadow-sm",
              thumb.animate && "transition-[translate,width] duration-(--gx-slide) ease-(--gx-ease-out) motion-reduce:transition-none",
            )}
            style={{ width: thumb.width, translate: `${thumb.x}px 0` }}
          />
        )}
        {options.map((option, i) => {
          const selected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role={tabs ? "tab" : "radio"}
              data-option=""
              data-selected={selected || undefined}
              aria-checked={tabs ? undefined : selected}
              aria-selected={tabs ? selected : undefined}
              tabIndex={i === focusable(options, value) ? 0 : -1}
              title={option.title}
              disabled={option.disabled}
              aria-disabled={option.blocked || undefined}
              onClick={() => onChange(option.value)}
              className={clsx(
                "inline-flex min-w-0 items-center justify-center gap-1.5 rounded-md font-medium transition-colors disabled:opacity-40",
                option.blocked && "opacity-40",
                size === "sm" ? "h-6 text-meta" : "h-7 text-ui",
                fill === "natural" ? "flex-auto px-1.5" : fill ? "flex-1 px-1" : size === "sm" ? "px-2" : "px-2.5",
                slide && "relative",
                selected ? clsx("text-accent", !(slide && thumb) && "bg-surface shadow-sm") : "text-ink-muted hover:text-ink",
              )}
            >
              {option.dot && <span className="size-1.5 shrink-0 rounded-full" style={{ background: option.dot }} aria-hidden />}
              {option.icon !== undefined && <span className="flex shrink-0">{renderIcon(option.icon, size === "sm" ? 12 : 14)}</span>}
              <span className="min-w-0 truncate">{option.label}</span>
              {option.count !== undefined && (
                <Badge size="sm" tone={selected ? "accent" : "neutral"}>
                  {option.count}
                </Badge>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Onglets d'un volet (R8) : onglet actif en `bg-selected`, retour à la ligne quand la place manque (jamais de
 *  défilement caché), compteur en `Badge`. Flèches, Début et Fin au clavier. `idOf` : identifiant du panneau
 *  piloté par chaque onglet (`aria-controls`), facultatif. */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  ariaLabel,
  idOf,
  className,
}: {
  tabs: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
  idOf?: (value: T) => string;
  /** Marges seulement. */
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={clsx("flex flex-wrap gap-1", className)}
      onKeyDown={(e) => arrowNavigate(e, tabs, value, onChange)}
    >
      {tabs.map((tab, i) => {
        const selected = value === tab.value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            data-option=""
            aria-selected={selected}
            aria-controls={idOf?.(tab.value)}
            tabIndex={i === focusable(tabs, value) ? 0 : -1}
            title={tab.title}
            disabled={tab.disabled}
            onClick={() => onChange(tab.value)}
            className={clsx(
              "inline-flex h-7 items-center gap-1.5 rounded-md px-3 text-ui font-medium whitespace-nowrap transition-colors disabled:opacity-40",
              selected ? "bg-selected text-ink" : "text-ink-muted hover:bg-hover hover:text-ink",
            )}
          >
            {tab.dot && <span className="size-1.5 shrink-0 rounded-full" style={{ background: tab.dot }} aria-hidden />}
            {tab.icon !== undefined && <span className="flex shrink-0">{renderIcon(tab.icon, 14)}</span>}
            {tab.label}
            {tab.count !== undefined && <Badge size="sm">{tab.count}</Badge>}
          </button>
        );
      })}
    </div>
  );
}
